import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateReminderModal from "../components/createReminderModal";
import { paths } from "../routes/paths";
import { CalendarIcon1Svg } from "../svgs/calendarIcon1Svg";
import { DiscordIcon1Svg } from "../svgs/discordIcon1Svg";
import { RemindersIconSvg } from "../svgs/remindersIconSvg";
import { PaymentRequestIconSvg } from "../svgs/paymentRequestIconSvg";
import { ServicesIconSvg } from "../svgs/servicesIconSvg";
import { EmployeesIconSvg } from "../svgs/employeesIconSvg";
import { SettingsIcon1Svg } from "../svgs/settingsIcon1Svg";
import { SearchIconSvg } from "../svgs/searchIconSvg";
import { NotificationSvg } from "../svgs/notificationSvg";
import PROFILE from "../api/services/PROFILE";
import SUBSCRIPTION from "../api/services/SUBSCRIPTION";
import BILLING from "../api/services/BILLING";
import NOTIFICATIONS from "../api/services/NOTIFICATIONS";
import MENU_VISIBILITY, { isMenuItemVisible } from "../api/services/MENU_VISIBILITY";
import { BASE_URL } from "../config";
import { Helmet } from "react-helmet";
import TourAvatarButton from "../guidedTour/components/TourAvatarButton";
import styles from "../styles/_layouts/header.module.scss";

// ─── Nav config ───────────────────────────────────────────────────────────────
export const NAV_ITEMS = [
  {
    id: "appointments",
    label: "תורים",
    icon: (fill) => <CalendarIcon1Svg fill={fill} />,
    path: paths.appointments,
    children: [
      { label: "יומן תורים", path: paths.appointments },
      { label: "קביעת תור", path: paths.createAppointment },
      { label: "בקשות לאישור", path: paths.appointmentRequests },
      { id: 'receptionDays', label: "הגדרות קבלת קהל", path: paths.workingHours(JSON.parse(localStorage.getItem("user_id") || '""')) },
      { label: "שינוי זמני", path: paths.scheduleOverride },
    ],
  },
  {
    id: "clients",
    label: "לקוחות",
    icon: (fill) => <DiscordIcon1Svg fill={fill} />,
    path: paths.clients,
    children: [
      { label: "רשימת לקוחות", path: paths.clients },
      { id: 'memberships', label: "מנויים", path: paths.memberShips },
      { id: 'allMemberships', label: "מנויים פעילים", path: paths.allMemberships },
    ],
  },
  {
    id: "messaging",
    label: "הודעות",
    icon: (fill) => <RemindersIconSvg fill={fill} />,
    path: paths.createNotifications,
    children: [
      { id: 'sendMessage', label: "שליחת עדכון", path: paths.createNotifications },
      { label: "תבניות הודעה", path: paths.messageTemplates },
      { label: "הודעות יוצאות", path: paths.messageHistory },
      { label: "התראות מערכת", path: paths.notifications },
      { id: 'reminders', label: 'תזכורות', path: paths.reminders },
      { id: 'voiceAssistant', label: 'עוזר קולי', path: '/voice-assistant' },
    ],
  },
  {
    id: "billing",
    label: "קופה בעסק",
    icon: (fill) => <PaymentRequestIconSvg fill={fill} />,
    path: paths.documents,
    children: [
      { id: 'cashRegister', label: "קופה וחשבוניות", path: paths.documents },
      { label: "רכישות", path: paths.purchases },
      { label: "קופונים", path: paths.coupons },
      { label: "גיפט קארד", path: paths.giftCards },
      { id: 'products', label: 'מוצרים', path: paths.products },
      { id: 'whatsappBot', label: 'בוט וואטסאפ', path: paths.whatsappBot },
    ],
  },
  {
    id: "services",
    label: "שירותים",
    icon: (fill) => <ServicesIconSvg fill={fill} />,
    path: paths.services,
    children: [
      { label: "השירותים שלי", path: paths.services },
    ],
  },
  {
    id: "employees",
    label: "עובדים",
    icon: (fill) => <EmployeesIconSvg fill={fill} />,
    path: paths.employees,
    children: [],
  },
  {
    id: "automations",
    label: "אוטומציות",
    icon: (fill) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={fill} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    path: paths.automations,
    children: [],
  },
  {
    id: "reviews",
    label: "ביקורות",
    icon: (fill) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={fill || "none"} stroke={fill || "#6b7280"} strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    path: paths.reviews,
    children: [],
  },
  {
    id: "settings",
    label: "הגדרות",
    icon: (fill) => <SettingsIcon1Svg fill={fill} />,
    path: paths.settings,
    children: [
      { id: 'calendarSettings', label: "הגדרות יומן ותורים", path: paths.settings },
      { label: "הגדרות סליקה", path: paths.paymentSettings, requiresBilling: true },
      { id: 'subscriptionInvoices', label: "המנוי שלי וחשבוניות", path: paths.invoices },
      { label: "טפסים ושאלונים", path: paths.forms },
      { id: 'reports', label: "דוחות", path: paths.stats },
      { id: 'reportsFiles', label: "דוחות וקבצים", path: paths.openfrmtExport },
      { id: 'help', label: "עזרה", path: paths.contact },
    ],
  },
];

// ─── Super-admin menu visibility ─────────────────────────────────────────────
// Maps the backend's canonical menu keys (Backend/src/config/appMenuItems.js)
// onto this dashboard's nav `id`s — top-level entries and submenu entries alike.
// Backend keys with no dashboard equivalent (popup_messages,
// voice_assistant_home, ai_receptionist_home, morning_invoice_home,
// tour_store_payments) are simply absent here and therefore ignored, and nav
// entries with no key (or no id at all) are never gated.
export const MENU_KEY_BY_NAV_ID = {
  // Top-level sidebar entries.
  services: "lessons_services",       // "שירותים"
  employees: "employees",             // "עובדים"
  billing: "store_payments",          // "קופה בעסק" == mobile "חנות ותשלומים"
  // Submenu entries.
  receptionDays: "reception_days",
  memberships: "subscriptions",
  allMemberships: "subscriptions",
  sendMessage: "send_message",
  cashRegister: "store_pos",
  whatsappBot: "whatsapp_assistant",
  calendarSettings: "calendar_settings",
  subscriptionInvoices: "billing",    // mobile "חיוב ושימוש"
  reports: "reports",
  reportsFiles: "reports",
  help: "help",
};

/**
 * Filter NAV_ITEMS through the super-admin visibility map.
 * FAIL-OPEN: a null/unknown map (not loaded yet, request failed, bad payload)
 * returns NAV_ITEMS untouched, so nothing is ever hidden by a flaky request.
 */
export function resolveVisibleNavItems(visibility) {
  if (!visibility) return NAV_ITEMS;
  const isVisible = (navId) =>
    isMenuItemVisible(visibility, MENU_KEY_BY_NAV_ID[navId]);

  const result = [];
  for (const item of NAV_ITEMS) {
    if (!isVisible(item.id)) continue;
    const children = item.children.filter((c) => isVisible(c.id));
    // A section that had children and lost every one of them has nothing left
    // to show. Sections that never had children (עובדים, אוטומציות, ביקורות)
    // are unaffected.
    if (item.children.length > 0 && children.length === 0) continue;
    // The section's own path may point at a child that is now hidden — land on
    // the first surviving child instead of a screen we just hid.
    const base = (p) => p.split("?")[0];
    const ownPathHidden =
      item.children.length > 0 &&
      !children.some((c) => base(c.path) === base(item.path));
    result.push({
      ...item,
      children,
      path: ownPathHidden ? children[0].path : item.path,
    });
  }
  return result;
}

export function getActiveItemId(currentPath, items = NAV_ITEMS) {
  for (const item of items) {
    if (item.path === currentPath) return item.id;
    if (item.children.some((c) => c.path.split("?")[0] === currentPath)) return item.id;
  }
  return null;
}

// Check if a child nav item is "active" — handles query-param paths like /clients?new=1
function isChildActive(childPath, currentPathname, currentSearch) {
  const [base, query] = childPath.split("?");
  if (query) {
    return currentPathname === base && currentSearch.includes(query);
  }
  // No query on child → active only when on the base path WITHOUT a conflicting query
  const conflictingQuery = NAV_ITEMS
    .flatMap((i) => i.children)
    .find((c) => c.path.startsWith(base + "?"));
  if (conflictingQuery) {
    const [, cq] = conflictingQuery.path.split("?");
    return currentPathname === base && !currentSearch.includes(cq);
  }
  return currentPathname === base;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Header = ({ setIsOpen, onSubmenuChange }) => {
  const [businessImg, setBusinessImg] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [canUseBilling, setCanUseBilling] = useState(true);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  // Super-admin menu-visibility map. Seeded from the session cache so a header
  // remount (the layout is torn down on every navigation) doesn't re-flash the
  // hidden entries. null = unknown → fail-open, everything visible.
  const [menuVisibility, setMenuVisibility] = useState(() =>
    MENU_VISIBILITY.get_cached()
  );

  let path = useLocation();
  path = path.pathname;
  const search = useLocation().search;
  const navItems = useMemo(
    () => resolveVisibleNavItems(menuVisibility),
    [menuVisibility]
  );
  const activeItemId = getActiveItemId(path, navItems);
  const activeItem = navItems.find((i) => i.id === activeItemId);
  const hasSubmenu = activeItem?.children?.length > 0;

  const navigate = useNavigate();

  // Notify layout whether submenu is visible
  useEffect(() => {
    onSubmenuChange?.(hasSubmenu);
  }, [hasSubmenu]);

  // FETCH SUPER-ADMIN MENU VISIBILITY — once per session (the service caches
  // in memory), fire-and-forget. Any failure resolves to null and everything
  // stays visible.
  useEffect(() => {
    let cancelled = false;
    MENU_VISIBILITY.get_visibility()
      .then((map) => {
        if (!cancelled && map) setMenuVisibility(map);
      })
      // The service already fails open internally, but never leave this chain
      // unguarded: an unhandled rejection would land on the exact path that is
      // supposed to degrade quietly. No map → nothing hidden.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // FETCH PROFILE + BILLING CAPABILITIES
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await PROFILE.get();
        let busImg =
          res.data.profile &&
          (res.data.profile.includes("https://")
            ? res.data.profile
            : `${BASE_URL}/uploads/${res.data.profile}`);
        setBusinessImg(busImg);
        setBusinessName(res.data.business_name);
      } catch (error) {
        console.log(error);
        if (
          error?.response?.data?.message === "Invalid Token" ||
          error?.response?.data?.message === "A token is required for authentication"
        ) {
          localStorage.clear();
          navigate(paths.login);
        }
      }
    };
    const checkBilling = async () => {
      try {
        const [subRes, credRes] = await Promise.allSettled([
          SUBSCRIPTION.get_sub_details(),
          BILLING.get_payment_cred(),
        ]);
        let isPro = false;
        if (subRes.status === "fulfilled") {
          const mode = subRes.value?.data?.currentPlan?.planMode || "FREE";
          isPro = mode !== "FREE";
        }
        let hasClearing = false;
        if (credRes.status === "fulfilled") {
          const creds = credRes.value?.data?.data || credRes.value?.data || {};
          hasClearing = !!(creds.terminalNumber || creds.greenInvoiceApiKey);
        }
        setCanUseBilling(isPro && hasClearing);
      } catch (err) {
        console.log("billing check error:", err);
      }
    };
    const fetchNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.allSettled([
          NOTIFICATIONS.get_notifications(),
          NOTIFICATIONS.get_unread_count(),
        ]);
        if (notifRes.status === "fulfilled") {
          const items = notifRes.value?.data?.data || notifRes.value?.data || [];
          setNotifications(Array.isArray(items) ? items.slice(0, 10) : []);
        }
        if (countRes.status === "fulfilled") {
          const count = countRes.value?.data?.count ?? countRes.value?.data ?? 0;
          setUnreadCount(typeof count === "number" ? count : 0);
        }
      } catch (err) {
        console.log("notifications err:", err);
      }
    };
    fetchData();
    checkBilling();
    fetchNotifications();
  }, []);

  return (
    <React.Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{businessName && businessName} - מערכת ניהול</title>
      </Helmet>

      {/* ── Top bar ──────────────────────────────────────────────────── */}
      <div className={styles.topBar}>
        {/* Left: actions */}
        <div className={styles.topActions}>
          {/* Guided-tour entry point — sits inline in the header instead
              of floating above content. Clicking opens TourListSheet. */}
          <TourAvatarButton variant="inline" />
          <button
            className={styles.supportBtn}
            onClick={() => navigate(paths.contact)}
          >
            תמיכה
          </button>
          <button
            className={styles.supportBtn}
            onClick={() => navigate(paths.reminders)}
            style={{ background: "#f0f4ff", color: "#3b82f6" }}
          >
            <img src="/assets/clock-icon-1.svg" alt="" width={14} style={{marginLeft: 6, verticalAlign: "middle"}} />
            תזכורות
          </button>
          <div className={styles.notifWrapper}>
            <button
              className={styles.iconBtn}
              title="התראות"
              onClick={() => {
                setShowNotifDropdown(!showNotifDropdown);
                if (!showNotifDropdown && unreadCount > 0) {
                  NOTIFICATIONS.mark_as_read_notifications().then(() => {
                    setUnreadCount(0);
                    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                  });
                }
              }}
            >
              <NotificationSvg fill="#9ca3b4" />
              {unreadCount > 0 && (
                <span className={styles.notifBadge}>{unreadCount}</span>
              )}
            </button>
            {showNotifDropdown && (
              <div className={styles.notifDropdown}>
                <div className={styles.notifDropdownHeader}>
                  <span>התראות אחרונות</span>
                  <button onClick={() => navigate(paths.notifications)} className={styles.notifViewAll}>
                    הצג הכל
                  </button>
                </div>
                {notifications.length === 0 ? (
                  <div className={styles.notifEmpty}>אין התראות חדשות</div>
                ) : (
                  <div className={styles.notifList}>
                    {notifications.map((n, idx) => {
                      const typeLabel =
                        n.type === "URGENT" ? "הודעה חשובה" :
                        n.type === "REGULAR" ? "התראת מערכת" :
                        "עדכון מערכת";
                      const typeColor =
                        n.type === "URGENT" ? "#ef4444" :
                        n.type === "REGULAR" ? "#DE1468" :
                        "#3b82f6";
                      const time = n.createdAt
                        ? `${new Date(n.createdAt).toLocaleDateString("he-IL")} ${new Date(n.createdAt).toLocaleTimeString("he-IL", {hour: "2-digit", minute: "2-digit"})}`
                        : "";
                      return (
                        <div key={n._id || idx} className={`${styles.notifItem} ${!n.isRead ? styles.notifUnread : ""}`}>
                          <span className={styles.notifType} style={{color: typeColor}}>
                            <svg width="10" height="10" viewBox="0 0 10 10" style={{marginLeft: 4, verticalAlign: "middle"}}>
                              <circle cx="5" cy="5" r="5" fill={typeColor} />
                            </svg>
                            {typeLabel}
                          </span>
                          <p className={styles.notifText}>{n.text || ""}</p>
                          <span className={styles.notifTime}>{time}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Center: search */}
        <div className={styles.searchWrapper}>
          <SearchIconSvg fill="#6b7280" />
          <input
            className={styles.searchInput}
            placeholder="חיפוש..."
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            dir="rtl"
          />
        </div>

        {/* Right: logo + business name */}
        <div className={styles.topBrand}>
          <img src="/assets/logo.svg" alt="logo" className={styles.topLogo} />
          {businessName && (
            <span className={styles.topBusinessName}>{businessName}</span>
          )}
        </div>
      </div>

      {/* ── Submenu panel — always visible when active item has children ─ */}
      {hasSubmenu && (
        <div className={styles.submenuPanel}>
          <p className={styles.submenuTitle}>{activeItem.label}</p>
          <ul className={styles.submenuList}>
            {activeItem.children.map((child) => {
              const childLocked = child.requiresBilling && !canUseBilling;
              return (
                <li
                  key={child.path}
                  className={`${
                    isChildActive(child.path, path, search)
                      ? styles.submenuItemActive
                      : styles.submenuItem
                  } ${childLocked ? styles.submenuItemLocked : ""}`}
                  onClick={() => {
                    if (!childLocked) navigate(child.path);
                  }}
                  title={childLocked ? "נדרש מנוי Pro" : ""}
                >
                  {child.label}
                  {childLocked && " 🔒"}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Main sidebar ─────────────────────────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.wrapper}>
          {/* Mobile title */}
          <div>
            <img src="/assets/logo.svg" alt="logo" className={styles.logo} />
            <span>מערכת ניהול</span>
          </div>

          {/* NAV ITEMS */}
          <nav>
            {navItems.map((item) => {
              const isActive = activeItemId === item.id;
              const isLocked = item.requiresBilling && !canUseBilling;
              const fill = isLocked
                ? "#d1d5db"
                : isActive
                ? "#de1468"
                : "#9ca3b4";
              // Attach data-tour attributes so the guided-tour spotlight can
              // find these nav items. Only a few nav items are tour targets.
              const tourAttr =
                item.id === "services"     ? "my-business-link"    :
                item.id === "availability" ? "hours-day-row"       :
                item.id === "appointments" ? "new-appointment-btn" : undefined;
              return (
                <li
                  key={item.id}
                  data-tour={tourAttr}
                  className={`${isActive ? styles.active : ""} ${isLocked ? styles.locked : ""}`}
                  onClick={() => {
                    if (isLocked) {
                      navigate(paths.paymentSettings);
                    } else {
                      navigate(item.path);
                    }
                  }}
                  title={isLocked ? "נדרש מנוי Pro והגדרת סליקה" : ""}
                >
                  {item.icon(fill)}
                  <p>{item.label}{isLocked ? " 🔒" : ""}</p>
                </li>
              );
            })}
          </nav>

          {/* PROFILE */}
          <div className={styles.profileSection}>
            <li style={{ listStyle: "none" }} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              {businessImg ? (
                <img src={businessImg} alt="profile" className={styles.userImg} />
              ) : (
                <img src="/assets/placeholder.png" alt="placeholder" className={styles.userImg} />
              )}
              <p className={styles.profileName}>{businessName || "פרופיל"}</p>
              {isDropdownOpen && (
                <div className={styles.userDropdown}>
                  <ul>
                    <li onClick={() => navigate(paths.profile)}>
                      <img src="/assets/user-icon-1.svg" alt="profile" />
                      <p>פרופיל</p>
                    </li>
                    <li onClick={() => { localStorage.clear(); navigate(paths.login); }}>
                      <img src="/assets/logout.svg" alt="logout" />
                      <p>התנתקות</p>
                    </li>
                  </ul>
                </div>
              )}
            </li>
          </div>

          {/* HAMBURGER — mobile */}
          <img
            src="/assets/menu.svg"
            alt="menu"
            className={styles.menu}
            onClick={() => setIsOpen(true)}
          />
        </div>
      </header>

      {/* Reminder quick-create modal */}
      {isReminderModalOpen && (
        <CreateReminderModal
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default Header;
